// client/src/pages/userGate.jsx

import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';

import ProfileHeader from '../../components/headers/profileHeader';
import SharedHeader from '../../components/headers/sharedHeader1';
import TwoFactorModal from '../../modals/auth/twoFactorModal';
import CustomAlert from '../../components/customs/customAlert';
import showNotification from '../../utilities/showNotification';
import { GlobalContext } from '../../context/provider';
import { mergeBackendDomains } from '../../types/courseDomains';


import { useAuth, useCourse, useStudent, useInstructor } from '../../hooks';
// import useStudent from '../../hooks/useStudent';
// import useAuth from '../../hooks/useAuth';
// import useInstructor from '../../hooks/useInstructor';
import authAction from "../../context/actions/authAction";
import { ADMIN_LOGIN_ID } from '../../constants/roles';

import '../../styles/globalstyles.css';
import './userGate.css';

const UserGate = () => {

  const { getStudentByLoginId } = useStudent(); // Hook called here
  const { getInstructorByLoginId } = useInstructor(); // Hook called here
  const [authChecked, setAuthChecked] = useState(false);  // ✅ ADD THIS
  const [show2FAPrompt, setShow2FAPrompt] = useState(false);
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [targetDashboard, setTargetDashboard] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  const [pendingDashboard, setPendingDashboard] = useState('');
  // const [showAlert, setShowAlert] = useState(false);
  
  const navigate = useNavigate();
  const { 
    authState: { authData, isLoggedIn }, 
    authDispatch,
  } = useContext(GlobalContext);
  
  const [isChecking, setIsChecking] = useState(true);
  const { check2FAStatus } = useAuth();

  // Inside UserGate component:
  const { getCourseDomains } = useCourse();

  useEffect(() => {
    getCourseDomains()
      .then(res => {
        if (res?.success) {
          mergeBackendDomains(res.data);
        }
      })
      .catch(err => console.warn('Could not load course domains:', err));
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // STEP 1: Verify session with backend
        console.log('🔍 Checking authentication...');
        const response = await fetch('/api/auth/session', { credentials: 'include' });
        
        if (!response.ok) {
          console.log('❌ Not authenticated - redirecting to login');
          navigate('/');
          return;
        }

        const data = await response.json();

        if (!data.authenticated || !data.user) {
          console.log('❌ Not authenticated - redirecting to login');
          navigate('/');
          return;
        }

        console.log('✅ User authenticated from session:', data.user.email);

        // STEP 2: Build base authData from session (role will be overwritten below)
        let authData = {
          email: data.user.email,
          id: data.user.id,
          name: data.user.name,
          photo: data.user.photo,
          picture: data.user.picture,
          role: 'student' // default, will be overwritten
        };

        // STEP 3: Detect correct role
        if (data.user.id === ADMIN_LOGIN_ID) {
          // STEP 3a: Admin check
          console.log('✅ user is admin');
          authData.role = 'admin';
          localStorage.setItem('userRole', 'admin');
          showNotification('👋 Welcome back, Admin');

        } else {
          // STEP 3b: Student check
          const studentResponse = await getStudentByLoginId(data.user.id);
          if (studentResponse.success && studentResponse.data) {

            if (studentResponse.data.accountStatus === 'deleted') {
              console.log('⚠️ Account is deleted');
              setAuthChecked(true);
              setIsChecking(false);
              return;
            }

            console.log('✅ Student profile found');
            authData.role = 'student';
            localStorage.setItem('userRole', 'student');

          } else {
            // STEP 3c: Instructor check
            const instructorResponse = await getInstructorByLoginId(data.user.id);
            if (instructorResponse.success && instructorResponse.data) {
              console.log('✅ Instructor profile found');
              authData.role = 'instructor';
              localStorage.setItem('userRole', 'instructor');

            } else {
              // STEP 3d: No profile found - show create profile screen
              console.log('❌ No profile found - need to create');
              authDispatch({ type: 'LOGIN_SUCCESS', payload: authData });
              localStorage.setItem('userInfo', JSON.stringify(authData));
              setAuthChecked(true);
              setIsChecking(false);
              return;
            }
          }
        }

        // STEP 4: Dispatch correct authData with correct role and save to localStorage
        authDispatch({ type: 'LOGIN_SUCCESS', payload: authData });
        localStorage.setItem('userInfo', JSON.stringify(authData));
        console.log('✅ [USERGATE] After LOGIN_SUCCESS dispatch, authData:', authData);
        setAuthChecked(true);

        // STEP 5: Skip navigation if already on a valid route (e.g. refresh on /admin/courses)
        const currentPath = window.location.pathname;
        if (currentPath.includes('/student/course') || 
            currentPath.includes('/instructor/course') || 
            currentPath.includes('/admin/')) {
          console.log('✅ Already on valid route, skipping navigation');
          setIsChecking(false);
          return;
        }

        // STEP 6: Determine dashboard path based on role
        const dashboardPath = {
          admin: '/admin/dashboard',
          instructor: '/instructor/dashboard',
          student: '/student/dashboard'
        }[authData.role];

        // STEP 7: Check 2FA and navigate
        const neverShow = localStorage.getItem(`2fa_prompt_never_${authData.id}`);
        if (neverShow) {
          navigate(dashboardPath);
          setIsChecking(false);
          return;
        }

        const status = await check2FAStatus();
        if (status.success && !status.enabled) {
          console.log('✅ [2FA Prompt] Should show prompt!');
          setPendingDashboard(dashboardPath);
          setShowAlert(true);
        } else {
          navigate(dashboardPath);
        }

        setIsChecking(false);

      } catch (error) {
        console.error('❌ Auth check error:', error);
        setAuthChecked(true);
        setIsChecking(false);
        navigate('/');
      }
    };

    checkAuth();
  }, []); // ✅ Merged two previous useeffects to detect user info
  
  const handle2FAModalClose = () => {
    setShow2FAModal(false);
    // Navigate to the stored target dashboard
    if (targetDashboard) {
      navigate(targetDashboard);
      setTargetDashboard('');
    }
  };

  // ✅ Handle successful 2FA setup
  const handle2FASuccess = () => {
    console.log('✅ 2FA enabled successfully');
  };



  useEffect(() => {
    const check2FA = async () => {
      console.log('🔍 [2FA Prompt] Starting check...');
      console.log('🔍 [2FA Prompt] authData:', authData);
      console.log('🔍 [2FA Prompt] isLoggedIn:', isLoggedIn);
      console.log('🔍 [2FA Prompt] isChecking:', isChecking);
      
      if (!authData?.id || !isLoggedIn) {
        console.log('❌ [2FA Prompt] No authData or not logged in');
        return;
      }
      
      // Don't show if already dismissed this session
      const dismissed = sessionStorage.getItem(`2fa_prompt_dismissed_${authData.id}`);
      console.log('🔍 [2FA Prompt] Dismissed this session?', dismissed);
      if (dismissed) {
        console.log('❌ [2FA Prompt] Already dismissed');
        return;
      }
      
      // Don't show if user permanently disabled it
      const neverShow = localStorage.getItem(`2fa_prompt_never_${authData.id}`);
      console.log('🔍 [2FA Prompt] Never show?', neverShow);
      if (neverShow) {
        console.log('❌ [2FA Prompt] User chose never show');
        return;
      }
      
      // Check 2FA status
      console.log('🔍 [2FA Prompt] Checking 2FA status...');
      const status = await check2FAStatus();
      console.log('🔍 [2FA Prompt] 2FA status:', status);
      
      if (status.success && !status.enabled) {
        console.log('✅ [2FA Prompt] Should show prompt!');
        setShow2FAPrompt(true);
      } else {
        console.log('❌ [2FA Prompt] 2FA already enabled or check failed');
      }
    };
    
    if (!isChecking && authData?.id) {
      console.log('🔍 [2FA Prompt] Conditions met, running check2FA()');
      check2FA();
    } else {
      console.log('❌ [2FA Prompt] Conditions not met. isChecking:', isChecking, 'authData.id:', authData?.id);
    }
  }, [authData?.id, isChecking, isLoggedIn, check2FAStatus]);

  const handleLogout = () => {
    try {
      // ⭐ Clear stored role
      authDispatch({type: 'LOGOUT', payload: ''});
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // ✅ Handle alert confirm (user wants to enable 2FA)
  const handleAlertConfirm = () => {
    setShowAlert(false);
    setTargetDashboard(pendingDashboard);
    setShow2FAModal(true);
  };

  // ✅ Handle alert cancel (user skips 2FA)
  const handleAlertCancel = () => {
    setShowAlert(false);
    if (pendingDashboard) {
      navigate(pendingDashboard);
      setPendingDashboard('');
    }
  };

  if (!authChecked || isChecking) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: '20px'
      }}>
        <div className="spinner"></div>
        <div>Checking your profile...</div>
      </div>
    );
  }

  return (
    <div className="main-container">

      {/* ✅ Custom Alert for 2FA Prompt */}
      <CustomAlert
        visible={showAlert}
        title="Two-Factor Authentication"
        icon="🔐"
        message="Would you like to enable 2FA for extra security? This adds protection to your account and takes less than 2 minutes to set up."
        onConfirm={handleAlertConfirm}
        onCancel={handleAlertCancel}
      />
      
      {/* ✅ 2FA Setup Modal */}
      {show2FAModal && (
        <TwoFactorModal 
          onClose={handle2FAModalClose}
          onSuccess={handle2FASuccess}
        />
      )}
      
      <div className="main-content">
        {/* Header Section */}
        <div className="header-row">
          <ProfileHeader data={authData} activePage="usergate" />
          <div className="headerMiddle">
            <img src="/images/prof_ix.png" alt="Logo" className="header-logo" />
          </div>
          <SharedHeader notificationCount={5} icons={[]} />
        </div>
        
        <div className="page-title">
          <h2>🎓 Welcome to IoT Training Platform</h2>
          <h3> Hello, {authData?.name}!</h3>
          {authData?.photo ? (
            <img 
              src={authData.photo} 
              alt={authData?.name} 
              className="avatar usergate-avatar-large"
            />
          ) : (
            <div className="usergate-avatar-placeholder">
              {authData?.name?.charAt(0) || 'U'}
            </div>
          )}
          <h2 className="usergate-title">Profile Setup Required</h2>
          
          {/* <p className="usergate-description">
            You haven't created a profile yet. Let's get you started with a quick setup!
          </p> */}
        </div>

        <div className="scrollable-content">
          <div className="info-stats">
            <div className="info-item">
              <span className="info-icon">📚</span>
              <strong>Access Courses</strong>
            </div>
            <div className="info-item">
              <span className="info-icon">🎯</span>
              <strong>Track Progress</strong>
            </div>
            <div className="info-item">
              <span className="info-icon">💬</span>
              <strong>Connect & Chat</strong>
            </div>
            <div className="info-item">
              <span className="info-icon">🏆</span>
              <strong>Earn Certificates</strong>
            </div>
          </div>

          <div className="action-buttons">
            <button
              onClick={() => navigate('/create-profile')}
              className="general-btn primary-btn"
            >
              <span className="btn-icon">✨</span>
              Create Profile
            </button>
            
            <button
              onClick={handleLogout}
              className="general-btn secondary-btn"
            >
              <span className="btn-icon">🚪</span>
              Logout
            </button>
          </div>
        </div>
        
      </div>
    </div>
  );
};

export default UserGate;